import { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Send, Search, BookOpen, Quote, ArrowLeft, MessageSquare } from "lucide-react";
import { toast } from "sonner";

function MessageCard({ m, mine }) {
  const bubble = mine ? "bg-terra text-white" : "bg-surface border border-line text-ink";
  if (m.tur === "book" && m.kart) {
    return (
      <Link to={`/kitap/${m.kart.id}`} data-testid="dm-book-card" aria-label="Kitap kartı" className={`block rounded-xl p-3 max-w-[80%] ${bubble}`}>
        <p className="text-[10px] uppercase tracking-wider opacity-70 flex items-center gap-1"><BookOpen size={11} /> Kitap</p>
        <p className="font-serif text-base leading-snug mt-1">{m.kart.baslik}</p>
        <p className="text-xs opacity-80">{m.kart.yazar}</p>
      </Link>
    );
  }
  if (m.tur === "quote" && m.kart) {
    return (
      <Link to={m.kart.book_id ? `/kitap/${m.kart.book_id}` : "#"} data-testid="dm-quote-card" aria-label="Alıntı kartı" className={`block rounded-xl p-3 max-w-[80%] ${bubble}`}>
        <p className="text-[10px] uppercase tracking-wider opacity-70 flex items-center gap-1"><Quote size={11} /> Alıntı</p>
        <p className="font-serif italic text-base leading-snug mt-1">"{m.kart.icerik}"</p>
        <p className="text-xs opacity-80 mt-1">— {m.kart.yazar_adi}</p>
      </Link>
    );
  }
  return <div className={`rounded-2xl px-4 py-2 max-w-[80%] text-sm leading-relaxed ${bubble}`}>{m.icerik}</div>;
}

function BookShareDialog({ onShare }) {
  const [q, setQ] = useState("");
  const [res, setRes] = useState([]);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (q.trim().length < 2) { setRes([]); return; }
    const t = setTimeout(() => api.get("/search", { params: { q } }).then((r) => setRes(r.data.kitaplar)), 250);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="share-book-btn" className="shrink-0 w-10 h-10 rounded-full border border-line flex items-center justify-center text-terra hover:bg-surface-alt"><BookOpen size={18} /></button>
      </DialogTrigger>
      <DialogContent className="bg-surface">
        <DialogHeader><DialogTitle className="font-serif text-2xl">Kitap Paylaş</DialogTitle></DialogHeader>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
          <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Kitap ara..." className="pl-9" data-testid="share-book-search" />
        </div>
        <div className="max-h-72 overflow-y-auto space-y-1">
          {res.map((b) => (
            <button key={b.id} data-testid={`share-book-${b.id}`} onClick={() => { onShare(b.id); setOpen(false); setQ(""); }}
              className="w-full text-left p-2.5 rounded-md hover:bg-surface-alt border border-line">
              <p className="text-sm font-medium text-ink">{b.baslik}</p>
              <p className="text-xs text-muted-foreground">{b.yazar}</p>
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function QuoteShareDialog({ onShare }) {
  const [q, setQ] = useState("");
  const [res, setRes] = useState([]);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => api.get("/posts/quotes", { params: q.trim().length >= 2 ? { q } : {} }).then((r) => setRes(r.data)), 250);
    return () => clearTimeout(t);
  }, [q, open]);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="share-quote-btn" className="shrink-0 w-10 h-10 rounded-full border border-line flex items-center justify-center text-gold hover:bg-surface-alt"><Quote size={18} /></button>
      </DialogTrigger>
      <DialogContent className="bg-surface">
        <DialogHeader><DialogTitle className="font-serif text-2xl">Alıntı Paylaş</DialogTitle></DialogHeader>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
          <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Alıntı içinde ara..." className="pl-9" data-testid="share-quote-search" />
        </div>
        <div className="max-h-72 overflow-y-auto space-y-1">
          {res.length === 0 && <p className="text-sm text-muted-foreground p-2">Alıntı bulunamadı.</p>}
          {res.map((p) => (
            <button key={p.id} data-testid={`share-quote-${p.id}`} onClick={() => { onShare(p.id); setOpen(false); setQ(""); }}
              className="w-full text-left p-2.5 rounded-md hover:bg-surface-alt border border-line">
              <p className="font-serif italic text-sm text-ink leading-snug line-clamp-2">"{p.icerik}"</p>
              <p className="text-xs text-muted-foreground mt-1">— {p.yazar_adi}</p>
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Messages() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const activeId = params.get("u");
  const [threads, setThreads] = useState([]);
  const [convo, setConvo] = useState(null);
  const [text, setText] = useState("");
  const endRef = useRef(null);

  const loadThreads = useCallback(() => { api.get("/messages/threads").then((r) => setThreads(r.data)); }, []);
  useEffect(() => { loadThreads(); }, [loadThreads]);

  const loadConvo = useCallback(() => {
    if (!activeId) { setConvo(null); return; }
    api.get(`/messages/with/${activeId}`).then((r) => setConvo(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, [activeId]);
  useEffect(() => { loadConvo(); }, [loadConvo]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [convo]);

  const send = async (payload) => {
    try {
      await api.post(`/messages/with/${activeId}`, payload);
      setText("");
      loadConvo();
      loadThreads();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const sendText = (e) => { e.preventDefault(); if (text.trim()) send({ icerik: text, tur: "text" }); };

  const openThread = (id) => setParams({ u: id });

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6" data-testid="messages-page">
      <h1 className="font-serif text-3xl text-ink mb-4 flex items-center gap-2"><MessageSquare className="text-terra" size={24} /> Mesajlar</h1>
      <div className="grid md:grid-cols-[300px_1fr] gap-4 border border-line rounded-2xl overflow-hidden bg-surface min-h-[60vh]">
        {/* Threads */}
        <div className={`border-r border-line ${activeId ? "hidden md:block" : "block"}`} data-testid="threads-list">
          {threads.length === 0 && <p className="p-6 text-sm text-muted-foreground text-center">Henüz mesajın yok. Bir üyenin profilinden mesaj gönderebilirsin.</p>}
          {threads.map((t) => (
            <button key={t.other_id} onClick={() => openThread(t.other_id)} data-testid={`thread-${t.other_id}`}
              className={`w-full text-left flex items-center gap-3 p-3 border-b border-line/60 hover:bg-surface-alt transition-colors ${activeId === t.other_id ? "bg-surface-alt" : ""}`}>
              <div className="w-10 h-10 rounded-full bg-terra/10 border border-line flex items-center justify-center overflow-hidden shrink-0">
                {t.profil_resmi ? <img src={t.profil_resmi} alt="" className="w-full h-full object-cover" /> : <span className="font-serif text-terra">{t.ad_soyad?.[0]}</span>}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-ink truncate">{t.ad_soyad}</p>
                <p className="text-xs text-muted-foreground truncate">{t.son_mesaj}</p>
              </div>
              {t.okunmamis > 0 && <span className="bg-terra text-white text-[10px] rounded-full w-5 h-5 flex items-center justify-center">{t.okunmamis}</span>}
            </button>
          ))}
        </div>
        {/* Chat */}
        <div className={`flex flex-col ${activeId ? "block" : "hidden md:flex"}`} data-testid="chat-pane">
          {!convo ? (
            <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground p-6">Sohbet etmek için bir konuşma seç.</div>
          ) : (
            <>
              <div className="flex items-center gap-3 p-3 border-b border-line">
                <button onClick={() => setParams({})} className="md:hidden text-muted-foreground"><ArrowLeft size={18} /></button>
                <Link to={`/uye/${convo.other.id}`} className="flex items-center gap-2">
                  <div className="w-9 h-9 rounded-full bg-terra/10 border border-line flex items-center justify-center overflow-hidden">
                    {convo.other.profil_resmi ? <img src={convo.other.profil_resmi} alt="" className="w-full h-full object-cover" /> : <span className="font-serif text-terra">{convo.other.ad_soyad?.[0]}</span>}
                  </div>
                  <span className="font-medium text-ink text-sm">{convo.other.ad_soyad}</span>
                </Link>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-3 max-h-[52vh]" data-testid="chat-messages">
                {convo.mesajlar.map((m) => {
                  const mine = m.from_id === user.id;
                  return <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}><MessageCard m={m} mine={mine} /></div>;
                })}
                <div ref={endRef} />
              </div>
              {convo.can_send ? (
                <form onSubmit={sendText} className="flex items-center gap-2 p-3 border-t border-line">
                  <BookShareDialog onShare={(bid) => send({ tur: "book", ref_id: bid })} />
                  <QuoteShareDialog onShare={(pid) => send({ tur: "quote", ref_id: pid })} />
                  <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Mesaj yaz..." className="flex-1" data-testid="message-input" />
                  <Button type="submit" data-testid="send-message" className="bg-terra hover:bg-terra-hover text-white gap-1"><Send size={16} /></Button>
                </form>
              ) : (
                <p className="p-4 text-center text-sm text-muted-foreground border-t border-line">Bu kullanıcı yalnızca takip ettiği kişilerden mesaj alıyor.</p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
